import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Copy, Pencil, Trash2, FileCode } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCategoryImage } from "@/lib/categoryImages";
import { getCategoryGradient } from "@/lib/categoryGradients";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
interface DashboardMacroCardProps {
  item: any;
  viewMode: "grid" | "list";
  onDelete: (id: string) => void;
  onCopyCode?: (id: string) => void;
  currentUserId?: string;
}
export const DashboardMacroCard = ({
  item,
  viewMode,
  onDelete,
  onCopyCode,
  currentUserId
}: DashboardMacroCardProps) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isDraft = item.type === "draft";
  const isSavedByOthers = item.type === "saved" && item.owner_id !== currentUserId;
  const name = item.name || item.title || "Untitled";
  const description = item.short_desc || item.description || "No description";
  const slug = item.slug;
  const tags = item.tags || [];
  const screenshotUrl = item.screenshot_url;
  const editUrl = `/upload?edit=${item.id}`;
  const viewUrl = `/m/${slug}`;
  return <>
      {viewMode === "list" ? <div className={cn("flex items-center justify-between p-4 rounded-lg transition-colors cursor-pointer", isDraft ? "bg-muted/70" : "hover:bg-muted")}>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => !isDraft && slug && navigate(`/m/${slug}`)}>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="font-semibold text-base truncate">{name}</h3>
              {isDraft ? <Badge variant="outline" className="bg-muted text-muted-foreground shrink-0">
                  Draft
                </Badge> : <>
                  {item.is_published && item.is_approved && <Badge variant="secondary" className="shrink-0">Published</Badge>}
                  {item.is_published && !item.is_approved && <Badge variant="outline" className="shrink-0 bg-warning/10 text-warning border-warning/20">
                      Pending Review
                    </Badge>}
                </>}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1">{description}</p>
          </div>

          {/* Action Buttons - Hidden for saved macros from other users */}
          {!isSavedByOthers && <div className="flex items-center gap-2 ml-4 shrink-0">
              {onCopyCode && <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          onCopyCode(item.id);
        }} title="Copy code">
                  <Copy className="h-4 w-4" />
                </Button>}
              <Button variant="ghost" size="sm" asChild title="Edit" onClick={e => e.stopPropagation()}>
                <Link to={editUrl}>
                  <Pencil className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          setShowDeleteDialog(true);
        }} title="Delete">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>}
        </div> :
    // Grid view - matches MacroCard design
    <div className="group block h-full">
          <div className={cn("relative bg-card rounded-2xl overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 h-full flex flex-col cursor-pointer", isDraft && "opacity-75")}>
            {/* Preview Area with Overlay */}
            <div className={`h-32 flex items-center justify-center relative overflow-hidden ${screenshotUrl ? "bg-muted" : getCategoryImage(tags[0]) ? getCategoryGradient(tags[0]) : "bg-muted"}`} onClick={() => !isDraft && slug && navigate(`/m/${slug}`)}>
              {screenshotUrl ? <img src={screenshotUrl} alt={name} className="w-full h-full object-cover" /> : getCategoryImage(tags[0]) ? <img src={getCategoryImage(tags[0])!} alt={tags[0] || "Category"} className="w-32 h-32 object-contain" /> : <FileCode className="w-20 h-20 text-muted-foreground" />}
              
              {/* Status Badge */}
              {isDraft ? <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm text-foreground border-border/50 text-xs font-medium">
                    Draft
                  </Badge>
                </div> : tags.length > 0 ? <div className="absolute top-3 right-3">
                  
                </div> : null}

              {!isDraft && !item.is_approved && <div className="absolute bottom-3 right-3">
                  <Badge variant="secondary" className="bg-warning/90 backdrop-blur-sm text-warning-foreground border-warning/50 text-xs font-medium">
                    Pending Review
                  </Badge>
                </div>}
            </div>
            
            {/* Content */}
            <div className="px-5 pt-5 pb-2 space-y-2 flex-1 flex flex-col bg-muted" onClick={() => !isDraft && slug && navigate(`/m/${slug}`)}>
              <h3 className="font-semibold text-base line-clamp-1 text-foreground">
                {name}
              </h3>
              
              <p className="text-sm text-muted-foreground line-clamp-1 leading-relaxed min-h-[1.25rem]">
                {description}
              </p>
              
              {isSavedByOthers && <div className="flex items-center justify-between pt-2 mt-auto">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {onCopyCode && <Button variant="ghost" size="sm" onClick={e => {
                e.stopPropagation();
                onCopyCode(item.id);
              }} className="h-7 w-7 p-0" title="Copy code">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>}
                  </div>
                </div>}
            </div>

            {/* Action Buttons - Hidden for saved macros from other users */}
            {!isSavedByOthers && <div className="p-3 bg-muted flex gap-2">
                {onCopyCode && <Button variant="outline" size="sm" onClick={() => onCopyCode(item.id)} className="flex-1" title="Copy Code">
                    <Copy className="h-4 w-4" />
                  </Button>}
                <Button variant="outline" size="sm" asChild className="flex-1" title="Edit">
                  <Link to={editUrl}>
                    <Pencil className="h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)} className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>}
          </div>
        </div>}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {isDraft ? "Draft" : "Macro"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            onDelete(item.id);
            setShowDeleteDialog(false);
          }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};